/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * MonthlyCalendarView — premium month grid.
 *
 * A modern, borderless mobile calendar: rounded surface, brand-orange selected /
 * today highlights, per-day event dots (semantic status colours) and a compact
 * legend. Shared by the unified CalendarScreen (Management) and the tables
 * booking screen.
 *
 * @param {object}   props
 * @param {Array}    props.bookings        Events with a `dateFrom` + `status`.
 * @param {Date}     props.selectedDate    Currently-focused day.
 * @param {Function} props.onDateSelect    Called with the tapped Date.
 * @param {Function} props.onViewChange    Fired after a day tap (host may switch view).
 * @param {boolean}  props.refreshing      Pull-to-refresh spinner state.
 * @param {Function} props.onRefresh       Pull-to-refresh handler.
 * @param {boolean} [props.showHeader=true] Render the built-in month navigator. Hosts that
 *   already provide their own toolbar (e.g. CalendarScreen) pass `false` to avoid a duplicate.
 */
import React, { useState, useEffect, useMemo } from "react";
import { Text, TouchableOpacity, View, ScrollView, RefreshControl, StyleSheet } from "react-native";
import IonIcons from "react-native-vector-icons/Ionicons";
import { useTranslation } from "react-i18next";
import { usePremiumLegacyTheme } from "../helpers/modeScreenTheme";

// Semantic status colours for the per-day event dots + legend.
const STATUS_COLORS = {
    accepted: "#16A34A",
    pending: "#F59E0B",
    rejected: "#DC2626"
};

const MonthlyCalendarView = ({
                                 bookings = [],
                                 selectedDate,
                                 onDateSelect = () => {},
                                 onViewChange = () => {},
                                 refreshing,
                                 onRefresh,
                                 showHeader = true
                             }) => {
    const { t } = useTranslation();
    // Premium palette overlay (orange primary, airy surfaces) over the legacy theme.
    const { theme, isDark } = usePremiumLegacyTheme();
    const styles = useMemo(() => StyleSheet.create(buildStyles(theme, isDark)), [theme, isDark]);
    const [currentMonth, setCurrentMonth] = useState(new Date(selectedDate));
    const [calendarDays, setCalendarDays] = useState([]);

    // Keep the visible month in sync with the externally-selected date.
    useEffect(() => {
        const newMonth = new Date(selectedDate);
        setCurrentMonth(new Date(newMonth.getFullYear(), newMonth.getMonth(), 1));
    }, [selectedDate]);

    useEffect(() => {
        generateCalendarDays();
    }, [currentMonth, bookings]);

    // Build the 6-week grid (leading/trailing days from adjacent months, Monday-first).
    const generateCalendarDays = () => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();

        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        const firstDayOfWeek = firstDay.getDay();
        // Adjust so Monday is the first column (0 = Monday, 6 = Sunday).
        const adjustedFirstDayOfWeek = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

        const totalDays = lastDay.getDate();
        const totalCells = Math.ceil((totalDays + adjustedFirstDayOfWeek) / 7) * 7;

        const days = [];

        for (let i = 0; i < adjustedFirstDayOfWeek; i++) {
            const prevMonthDate = new Date(year, month, -adjustedFirstDayOfWeek + i + 1);
            days.push({ date: prevMonthDate, isCurrentMonth: false, bookings: getBookingsForDate(prevMonthDate) });
        }

        for (let i = 1; i <= totalDays; i++) {
            const date = new Date(year, month, i);
            days.push({ date, isCurrentMonth: true, bookings: getBookingsForDate(date) });
        }

        const remainingCells = totalCells - days.length;
        for (let i = 1; i <= remainingCells; i++) {
            const nextMonthDate = new Date(year, month + 1, i);
            days.push({ date: nextMonthDate, isCurrentMonth: false, bookings: getBookingsForDate(nextMonthDate) });
        }

        setCalendarDays(days);
    };

    const getBookingsForDate = (date) => {
        if (!bookings || bookings.length === 0) return [];

        return bookings.filter(booking => {
            const bookingDate = new Date(booking.dateFrom);
            return (
                bookingDate.getDate() === date.getDate() &&
                bookingDate.getMonth() === date.getMonth() &&
                bookingDate.getFullYear() === date.getFullYear()
            );
        });
    };

    const goToPreviousMonth = () => {
        const prevMonth = new Date(currentMonth);
        prevMonth.setMonth(prevMonth.getMonth() - 1);
        setCurrentMonth(prevMonth);
    };

    const goToNextMonth = () => {
        const nextMonth = new Date(currentMonth);
        nextMonth.setMonth(nextMonth.getMonth() + 1);
        setCurrentMonth(nextMonth);
    };

    const getMonthYearDisplay = () => currentMonth.toLocaleDateString('hu-HU', { year: 'numeric', month: 'long' });

    const isToday = (date) => {
        const today = new Date();
        return (
            date.getDate() === today.getDate() &&
            date.getMonth() === today.getMonth() &&
            date.getFullYear() === today.getFullYear()
        );
    };

    const isSelected = (date) => (
        date.getDate() === selectedDate.getDate() &&
        date.getMonth() === selectedDate.getMonth() &&
        date.getFullYear() === selectedDate.getFullYear()
    );

    const renderWeekdays = () => {
        const weekdays = [
            t('calendar_weekday_mon'),
            t('calendar_weekday_tue'),
            t('calendar_weekday_wed'),
            t('calendar_weekday_thu'),
            t('calendar_weekday_fri'),
            t('calendar_weekday_sat'),
            t('calendar_weekday_sun'),
        ];

        return (
            <View style={styles.weekdaysContainer}>
                {weekdays.map((day, index) => (
                    <View key={index} style={styles.weekdayCell}>
                        <Text style={[styles.weekdayText, index >= 5 && styles.weekdayWeekend]}>{day}</Text>
                    </View>
                ))}
            </View>
        );
    };

    const renderCalendarGrid = () => {
        const weeks = [];
        for (let i = 0; i < calendarDays.length; i += 7) {
            weeks.push(calendarDays.slice(i, i + 7));
        }

        return (
            <View style={styles.calendarGrid}>
                {weeks.map((week, weekIndex) => (
                    <View key={weekIndex} style={styles.weekRow}>
                        {week.map((day, dayIndex) => {
                            const today = isToday(day.date);
                            const selected = isSelected(day.date);

                            return (
                                <TouchableOpacity
                                    key={dayIndex}
                                    activeOpacity={0.7}
                                    style={styles.dayCell}
                                    onPress={() => {
                                        onDateSelect?.(day.date);
                                        onViewChange?.();
                                    }}
                                >
                                    <View style={[
                                        styles.dayPill,
                                        today && styles.todayPill,
                                        selected && styles.selectedPill
                                    ]}>
                                        <Text style={[
                                            styles.dayNumber,
                                            !day.isCurrentMonth && styles.outOfMonthText,
                                            today && styles.todayText,
                                            selected && styles.selectedText
                                        ]}>
                                            {day.date.getDate()}
                                        </Text>
                                    </View>

                                    <View style={styles.dotsRow}>
                                        {day.bookings.slice(0, 3).map((booking, index) => (
                                            <View
                                                key={index}
                                                style={[styles.bookingDot, { backgroundColor: STATUS_COLORS[booking.status] || STATUS_COLORS.pending }]}
                                            />
                                        ))}
                                        {day.bookings.length > 3 && (
                                            <Text style={styles.moreBookingsText}>+{day.bookings.length - 3}</Text>
                                        )}
                                    </View>
                                </TouchableOpacity>
                            );
                        })}
                    </View>
                ))}
            </View>
        );
    };

    return (
        <ScrollView
            style={styles.container}
            contentContainerStyle={styles.contentContainer}
            showsVerticalScrollIndicator={false}
            refreshControl={onRefresh ? (
                <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={theme.colors.primary} />
            ) : undefined}
        >
            {showHeader && (
                <View style={styles.header}>
                    <TouchableOpacity onPress={goToPreviousMonth} style={styles.navButton} activeOpacity={0.7}>
                        <IonIcons name="chevron-back" size={22} color={theme.colors.text} />
                    </TouchableOpacity>

                    <Text style={styles.headerText}>{getMonthYearDisplay()}</Text>

                    <TouchableOpacity onPress={goToNextMonth} style={styles.navButton} activeOpacity={0.7}>
                        <IonIcons name="chevron-forward" size={22} color={theme.colors.text} />
                    </TouchableOpacity>
                </View>
            )}

            <View style={styles.card}>
                {renderWeekdays()}
                {renderCalendarGrid()}
            </View>

            <View style={styles.legend}>
                <View style={styles.legendItem}>
                    <View style={[styles.legendIndicator, { backgroundColor: STATUS_COLORS.accepted }]} />
                    <Text style={styles.legendText}>{t('calendar_legend_accepted')}</Text>
                </View>
                <View style={styles.legendItem}>
                    <View style={[styles.legendIndicator, { backgroundColor: STATUS_COLORS.pending }]} />
                    <Text style={styles.legendText}>{t('calendar_legend_pending')}</Text>
                </View>
                <View style={styles.legendItem}>
                    <View style={[styles.legendIndicator, { backgroundColor: STATUS_COLORS.rejected }]} />
                    <Text style={styles.legendText}>{t('calendar_legend_rejected')}</Text>
                </View>
            </View>
        </ScrollView>
    );
};

const buildStyles = (theme, isDark) => ({
    container: {
        width: '100%'
    },
    contentContainer: {
        // Self-sufficient inset so the card never touches screen edges regardless of the
        // host's padding (CalendarScreen adds screen padding; TablesBookings adds none).
        paddingHorizontal: 8,
        paddingBottom: 8
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 6,
        paddingHorizontal: 4,
        marginBottom: 4
    },
    headerText: {
        fontSize: 18,
        fontWeight: '800',
        color: theme.colors.text,
        textTransform: 'capitalize'
    },
    navButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.card,
        borderWidth: 1,
        borderColor: theme.colors.border
    },
    // Rounded surface behind the whole month — the premium "card" look.
    card: {
        backgroundColor: theme.colors.card,
        borderRadius: 26,
        borderWidth: 1,
        borderColor: theme.colors.border,
        paddingHorizontal: 6,
        paddingVertical: 10,
        shadowColor: '#0F172A',
        shadowOffset: { width: 0, height: 6 },
        shadowOpacity: isDark ? 0.35 : 0.06,
        shadowRadius: 14,
        elevation: 2
    },
    weekdaysContainer: {
        flexDirection: 'row',
        marginBottom: 4
    },
    weekdayCell: {
        flex: 1,
        alignItems: 'center',
        paddingVertical: 6
    },
    weekdayText: {
        fontSize: 11,
        fontWeight: '800',
        letterSpacing: 0.5,
        textTransform: 'uppercase',
        color: theme.colors.subText
    },
    weekdayWeekend: {
        color: theme.colors.primary
    },
    calendarGrid: {
        width: '100%'
    },
    weekRow: {
        flexDirection: 'row'
    },
    // Borderless cell — modern calendars separate days with whitespace + a highlight
    // pill, not a hard grid. Fixed height keeps the 6-week grid a stable size.
    dayCell: {
        flex: 1,
        height: 52,
        alignItems: 'center',
        justifyContent: 'flex-start',
        paddingTop: 4
    },
    dayPill: {
        width: 36,
        height: 36,
        borderRadius: 18,
        alignItems: 'center',
        justifyContent: 'center'
    },
    todayPill: {
        backgroundColor: `${theme.colors.primary}26`
    },
    selectedPill: {
        backgroundColor: theme.colors.primary,
        shadowColor: theme.colors.primary,
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.4,
        shadowRadius: 8,
        elevation: 3
    },
    dayNumber: {
        fontSize: 14.5,
        fontWeight: '700',
        color: theme.colors.text
    },
    outOfMonthText: {
        color: theme.colors.placeholder,
        fontWeight: '500'
    },
    todayText: {
        color: theme.colors.primary,
        fontWeight: '900'
    },
    selectedText: {
        color: '#FFFFFF',
        fontWeight: '900'
    },
    dotsRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        height: 8,
        marginTop: 3
    },
    bookingDot: {
        width: 5,
        height: 5,
        borderRadius: 3
    },
    moreBookingsText: {
        fontSize: 9,
        fontWeight: '700',
        color: theme.colors.subText,
        marginLeft: 1
    },
    legend: {
        flexDirection: 'row',
        justifyContent: 'center',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 14
    },
    legendItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 999,
        backgroundColor: theme.colors.card,
        borderWidth: 1,
        borderColor: theme.colors.border
    },
    legendIndicator: {
        width: 9,
        height: 9,
        borderRadius: 5
    },
    legendText: {
        fontSize: 12,
        fontWeight: '600',
        color: theme.colors.subText
    }
});

export default MonthlyCalendarView;
